/**
 * Scope parsing, serialization, and search utilities for AI chat handlebars ({{type:id}}).
 */

export const SCOPE_REF_REGEX = /\{\{(\w+):([^}]+)\}\}/g;

/** Return Set of "type:id" for every scope ref in serialized message string */
export function getScopeKeysFromSerialized(str) {
    const set = new Set();
    if (!str) return set;
    let m;
    const re = /\{\{(\w+):([^}]+)\}\}/g;
    while ((m = re.exec(str)) !== null) set.add(`${m[1]}:${m[2]}`);
    return set;
}

/** Resolve scope ref (type + id) to display label using classes/students lists */
export function resolveScopeLabel(type, id, classes, students) {
    if (type === 'class') {
        const c = (classes || []).find((x) => x.value === id);
        return c?.label ?? id;
    }
    if (type === 'student') {
        const s = (students || []).find((x) => String(x.id) === id);
        const first = s?.firstName ?? '';
        const last = s?.lastName ?? '';
        return [first, last].filter(Boolean).join(' ').trim() || id;
    }
    return id;
}

/** Serialize contenteditable DOM to message string with {{type:id}} for scope spans */
export function serializeContentEditable(container) {
    if (!container) return '';
    let out = '';
    const walk = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            out += node.textContent;
            return;
        }
        if (node.nodeType === Node.ELEMENT_NODE) {
            const el = node;
            if (el.getAttribute?.('data-entity-type') && el.getAttribute?.('data-entity-id') != null) {
                out += `{{${el.getAttribute('data-entity-type')}:${el.getAttribute('data-entity-id')}}}`;
                return;
            }
            el.childNodes.forEach(walk);
        }
    };
    container.childNodes.forEach(walk);
    return out;
}

/** Hydrate contenteditable with a message string: {{type:id}} → scope chips, other {{x}} → plain text */
export function hydrateContentEditable(container, messageStr, classes, students) {
    if (!container || messageStr == null) return;
    const parts = messageStr.split(/(\{\{[^}]+\}\})/g);
    container.textContent = '';
    parts.forEach((part) => {
        const scopeMatch = part.match(/^\{\{(student|class):([^}]+)\}\}$/);
        if (scopeMatch) {
            const [, type, id] = scopeMatch;
            const span = document.createElement('span');
            span.contentEditable = 'false';
            span.setAttribute('data-entity-type', type);
            span.setAttribute('data-entity-id', id);
            span.className = 'ai-chat-inline-scope';
            span.textContent = resolveScopeLabel(type, id, classes, students);
            container.appendChild(span);
        } else {
            container.appendChild(document.createTextNode(part));
        }
    });
}

/** Search students and classes by name; returns up to 6 matches { type, id, label } */
export function searchStudentsAndClasses(value, students, classes) {
    const q = (value || '').toLowerCase().trim();
    if (!q) return [];
    const out = [];
    const seen = new Set();
    (students || []).forEach((s) => {
        const first = (s.firstName || '').trim();
        const last = (s.lastName || '').trim();
        const full = [first, last].filter(Boolean).join(' ').toLowerCase();
        const match = full.includes(q) || first.toLowerCase().includes(q) || last.toLowerCase().includes(q);
        if (match && !seen.has(`student:${s.id}`)) {
            seen.add(`student:${s.id}`);
            out.push({
                type: 'student',
                id: String(s.id),
                label: [first, last].filter(Boolean).join(' ') || 'Unknown',
            });
        }
    });
    (classes || []).forEach((c) => {
        const label = (c.label || c.value || '').toLowerCase();
        if (label.includes(q) && !seen.has(`class:${c.value}`)) {
            seen.add(`class:${c.value}`);
            out.push({ type: 'class', id: c.value, label: c.label || c.value });
        }
    });
    return out.slice(0, 6);
}
